import React from 'react';
import ReactDOM from 'react-dom';
import IdleTimer from 'react-idle-timer';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'assets/css/app';
import styles from 'assets/css/base.module.css';
import '@nwea/nwea-frontend-components-v1/dist/design-system.css';

import Settings from 'resources/settings';
import Utils from 'utils';
import ReportAccessUtils from 'utils/reportAccess';
import tabDetails from './components/Navigation/tabDetails';
import reportDetails from './components/Navigation/reportDetails';
import ClientUtils from 'utils/http-clients';

import ReportingApi from 'http-clients/reporting-api';
import PartnerConfig from 'http-clients/partner-config';

import { AuthManager } from './authentication';

import MainContainer from './components/MainContainer';

import ErrorPage from 'components/ErrorPage';
import RLPError from 'components/ErrorPage/RLPError';

import BrowserContext from './components/BrowserContext';
import LicenseContext from './components/LicenseContext';

import { getLaunchDarklyClientSideID } from 'utils/LaunchDarkly';
import { withLDProvider } from 'launchdarkly-react-client-sdk';

import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

if (process.env.ENV_CHOICE === 'local' || process.env.ENV_CHOICE === 'automation') {
  const { worker } = require('./mocks/browser');
  worker.start({
    // Disables the logging of matched requests in a browser's console
    // quiet: true,
    // Specifies how to handle a request that is not listed in the request handlers
    // 'bypass' quiets messages, see https://mswjs.io/docs/api/setup-worker/start for more options
    // onUnhandledRequest: 'bypass',
  });
}

datadogRum.init({
  applicationId: 'd07bd4ae-faa2-4ae4-9158-78f9df0fa840',
  clientToken: 'pubef396eb48aa65c3e58feadc8f6c976b4',
  site: 'datadoghq.com',
  service: 'reports-landing-page',
  env: process.env.ENV_CHOICE,
  sessionSampleRate: process.env.ENV_CHOICE === 'production' ? 5 : 100,
  sessionReplaySampleRate: 0,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
});

datadogLogs.init({
  clientToken: 'pubef396eb48aa65c3e58feadc8f6c976b4',
  site: 'datadoghq.com',
  service: 'reports-landing-page',
  env: process.env.ENV_CHOICE,
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
});

const AUTOMATION = 'automation';
const LOCAL = 'local';

const App = {
  /*
      Main Application Process

      - initialize (declare globals, determine proceeding action)
      - load (session)
      - get (API data)
      - parse (API data)
      - ready (to render components)
      - execute (app; DOM rendered, declare global event listeners once to avoid bubbling & collisions)
  */

  init: async () => {
    console.log('App.init() The Reports landing page');

    // before we do anything redirect to a 404 if the supplied URL is not catered for within apigee's list
    // of oidc callbacks. Aat this point assume the user has access to all available tabs -
    // we will redirect to a 404 later if they do not have access to a particular tab - we need to log in first
    // to figure that out
    if (Utils.getTargetEndpoint(window.location.pathname, tabDetails.growthReports.urlEndPoint) === '404') {
      App.render(<ErrorPage thrownError={new RLPError({ code: '404' })} />);
    } else {
      // Initialize Application User
      App.User = {
        BROWSER: {}, // Browser information for CSS adjustments
        MENUS: {}, // NavMenu/Pancakes data via uap-gateway/userProfile/menus
        NAME: '',
        PROFILE: {},
        token: '', // API Authentication Token required for GET requests
        LICENSE: {
          // Contains license data
          pg1: null,
          mrl: null,
          sci: null,
          cm1: null,
        },
      };

      // Initialize Application State
      App.State = {
        modalOpen: false, // Modal Overlay Active|Hidden Status
      };

      // Initialize Application Globals/Constants
      App['TIMEOUT'] = 1000 * 60 * 30 * (process.env.ENV_CHOICE === LOCAL ? 48 : 1); // Logs user out if idle >= 30 mins (1 day if running locally)

      // Handle authentication tasks
      if (process.env.ENV_CHOICE !== AUTOMATION) {
        const authManagerSettings = App.AuthManagerSettings();
        App['AUTH_MANAGER'] = new AuthManager(authManagerSettings);
        try {
          await App['AUTH_MANAGER'].init(Settings.OIDC_CLIENT_ID);
        } catch (err) {
          console.error(err);
          location.href = authManagerSettings.START_PAGE_URL;
        }
      } else {
        App['AUTH_MANAGER'] = {
          session: {
            user: {
              token: 'mockToken',
            },
          },
        };
      }

      // If a session exists, then user has authenticated, so load the app
      if (App['AUTH_MANAGER'].session) {
        App.load(App['AUTH_MANAGER'].session);
      }
      // If a session does NOT exist, then the init function completes without rendering,
      // and the Auth Manager asynchronously redirects the user to the login page.
    }
  },

  load: async (session) => {
    console.log('App.load()');
    const profile = session.user.profile;

    // Set User info using userProfile/menus API response data
    App.User['BROWSER'] = Utils.browserInfo();
    App.User['NAME'] = `${profile.firstName} ${profile.lastName}`;
    App.User['PROFILE'] = profile;

    // Set Browser classes for CSS adjustments
    const browser = App.User['BROWSER'].name.toLowerCase();
    const version = 'v' + App.User['BROWSER'].version;
    const category = browser === 'safari' && App.User['BROWSER'].version <= 9 ? 'old-browser' : 'modern-browser';

    document.querySelector('html').classList.add(browser, version, category);
    const config = {
      headers: {
        Authorization: `Bearer ${session.user.token}`,
      },
    };

    const ACCESS_ATTEMPT = 'application access attempt';
    try {
      App.User['MENUS'] = await ClientUtils.makeGetRequest(Settings['USER_PROFILE'], config);

      App.get();
      sessionStorage.removeItem(ACCESS_ATTEMPT);
    } catch (err) {
      if (sessionStorage.getItem(ACCESS_ATTEMPT)) {
        const error = new RLPError(
          {},
          'There was a problem accessing the application. Please contact Customer Support.',
          err
        );
        error.log();
        App.render(<ErrorPage thrownError={error} />);
        // Also add an explicit console message to provide partner support with guidance to the underlying issue.
        console.error(
          `ROOT CAUSE OF ERROR: The 'user profile' service failed during a request for application menus '${Settings['USER_PROFILE']}'.`
        );
      } else {
        sessionStorage.setItem(ACCESS_ATTEMPT, 'FAILED');
        App['AUTH_MANAGER'].clearSession();
        App['AUTH_MANAGER'] = new AuthManager(App.AuthManagerSettings());
        App.init();
      }
    }
  },

  AuthManagerSettings: function () {
    /* Enable OIDC logging
     *  pass in OIDC_LOGGING level or boolean
     *  LEVEL: NONE = 0, ERROR = 1, WARN = 2, INFO = 3, DEBUG = 4;
     */
    const OIDC_LOGGING = ['development', 'integration'].includes(process.env.ENV_CHOICE) ? 3 : 0;
    return {
      UPDATE_FN: App.updateAuthToken,
      LOG_ERROR: RLPError,
      OIDC_LOGGING,
      ...Settings,
    };
  },

  get: async function () {
    // Get API data for Application here...

    const userProfile = App.User['PROFILE'];
    const userLicense = App.User['LICENSE'];
    const initialData = {
      dataExportHistory: [],
      userLicenseInfo: [],
      reportAccessExceptions: {},
    };

    const session = App['AUTH_MANAGER'].session;

    initialData.sessionToken = {
      headers: {
        Authorization: `Bearer ${session.user.token}`,
      },
    };

    //Load Data Export History
    try {
      const exportHistory = await ReportingApi.getDataExportHistory(
        userProfile.partnerBid,
        userProfile.userBid,
        initialData.sessionToken
      );

      if (Array.isArray(exportHistory)) {
        //Only add valid data from the api endpoint
        initialData.dataExportHistory = exportHistory;
      }
    } catch (err) {
      new RLPError({ payload: App.State }, 'failed to get initial application data', err).log();
    }

    //Load User Licenses
    let userLicenseInfo = {};
    try {
      userLicenseInfo = await ReportingApi.getUserLicenses(userProfile.partnerBid, initialData.sessionToken);

      initialData.userLicenseInfo =
        userLicenseInfo && userLicenseInfo['partnerLicenses'] && Object.keys(userLicenseInfo).length !== 0
          ? userLicenseInfo['partnerLicenses'].split(',')
          : [];
    } catch (err) {
      new RLPError({ payload: App.State }, 'failed to get user license data', err).log();
    }

    /* License Context for PG1 */
    if (userLicenseInfo.partnerLicenses) {
      const { partnerLicenses } = userLicenseInfo;
      if (partnerLicenses.includes('PG1')) {
        userLicense.pg1 = true;
      }
    }

    //Load Partner Configuration
    let partnerConfig = {};
    try {
      partnerConfig = await PartnerConfig.getSystemConfigurations(userProfile.partnerBid, initialData.sessionToken);

      initialData.reportAccessExceptions = partnerConfig.systemConfigurations;
    } catch (err) {
      new RLPError({ payload: App.State }, 'failed to get partner system configurations', err).log();
    }

    App.parse(initialData);
  },

  parse: async function (data) {
    const userProfile = App.User['PROFILE'];
    const userRoles = App.User['PROFILE'].uapRoles;
    const reportsForUser = ReportAccessUtils.getReportsForUser(
      userRoles,
      data.userLicenseInfo,
      data.reportAccessExceptions,
      userProfile.partnerBid
    );

    // Parse, massage, and otherwise adjust the data
    const appItems = { userAvailableTabs: Utils.getAvailableTabs(reportsForUser) };
    appItems.defaultSelectedTabEndpoint = Utils.getDefaultSelectedTabEndpoint(appItems.userAvailableTabs);
    appItems.userOperationalReportDetails = Utils.getUserOperationalReportDetails(reportsForUser);
    appItems.userGrowthReportDetails = Utils.getUserGrowthReportDetails(reportsForUser);

    appItems.dataExportHistory = data.dataExportHistory;
    appItems.sessionToken = data.sessionToken;
    App.User['APP_ITEMS'] = appItems;

    App.redirect(appItems.userAvailableTabs, appItems.defaultSelectedTabEndpoint);
  },

  redirect: function (userAvailableTabs, defaultSelectedTabEndpoint) {
    let contentType = null;

    const targetEndpoint = Utils.getTargetEndpoint(window.location.pathname, defaultSelectedTabEndpoint);

    if (targetEndpoint === '404') {
      contentType = new RLPError({
        code: '404',
        payload: {
          pathName: window.location.pathname,
          defaultSelectedTabEndpoint: defaultSelectedTabEndpoint,
        },
      });
      contentType.log();
    } else {
      const hasReportsForEndpoint = (reportsEndpoint) => {
        return (
          reportsEndpoint === reportDetails.recoveryGoalSetting.urlEndPoint ||
          userAvailableTabs.find((tabDetail) => {
            return tabDetail.urlEndPoint === reportsEndpoint;
          })
        );
      };

      if (userAvailableTabs.length > 1) {
        // the target endpoint is valid, and the user has some reports.
        // Ensure reports exist for the targetEndpoint before allowing access
        if (targetEndpoint !== tabDetails.reportsQueue.urlEndPoint && !hasReportsForEndpoint(targetEndpoint)) {
          contentType = new RLPError({
            code: '403',
            payload: { targetEndpoint: targetEndpoint, hasReports: false },
          });
          contentType.log();
        }
      } else {
        contentType = new RLPError({
          code: '403',
          payload: { targetEndpoint: targetEndpoint, hasAccess: false },
        });
        contentType.log();
      }
    }

    if (!contentType && !window.location.pathname.endsWith(targetEndpoint)) {
      // Change location to desired endpoint so that we no longer render the app multiple times
      location.href = `${Settings.BASE_URL}${Settings.URL_BASE_PATH}${targetEndpoint}`;
    } else {
      App.ready(contentType).exec();
    }
  },

  ready: function (contentType) {
    const content = contentType ? <ErrorPage thrownError={contentType} /> : App.generateContent();
    try {
      App.render(content);
    } catch (err) {
      new RLPError({ payload: { app: App.State, contentType: contentType } }, err).log();
      App.render(<ErrorPage thrownError={err} />);
    }
    return this;
  },

  exec: function () {
    console.log('App.exec()');

    // Global Event Handlers
    // window.addEventListener('<event>', <event-handler>);
    App.ROOT = document.getElementById('root');
    window.addEventListener('mousedown', App.hideFocusOutline);
    window.addEventListener('keydown', App.allowFocusOutline);
  },

  /*
      Main Application Methods
  */

  generateContent: function () {
    App.User['APP_ITEMS'].menus = App.User['MENUS'];
    App.User['APP_ITEMS'].userName = App.User['PROFILE'].username;
    App.User['APP_ITEMS'].partnerBid = App.User['PROFILE'].partnerBid;

    const clientSideID = getLaunchDarklyClientSideID(process.env.ENV_CHOICE);
    const user = {
      key: App.User['PROFILE'].partnerBid,
      //...App.User['PROFILE'], // Spread out the user info for analytics within LD
    };

    /* withLDProvider is a higher order function that will return a component which will then wrap another component - in this case, MainContainer
     * We will then render the newly wrapped MainContainer component */
    const LaunchDarklyWrappedMainContainer = withLDProvider({ clientSideID: clientSideID, user: user })(MainContainer);
    return (
      <IdleTimer element={document} onIdle={this.handleIdleAction} timeout={App['TIMEOUT']}>
        <BrowserContext.Provider value={App.User['BROWSER'].name.toLowerCase()}>
          <LicenseContext.Provider value={App.User['LICENSE']}>
            <LaunchDarklyWrappedMainContainer
              userRoles={App.User['PROFILE'].uapRoles}
              educatorBid={App.User['PROFILE'].userBid}
              appData={App.User['APP_ITEMS']}
            />
          </LicenseContext.Provider>
        </BrowserContext.Provider>
      </IdleTimer>
    );
  },

  updateAuthToken: function (newToken) {
    App.User['APP_ITEMS'].sessionToken = {
      headers: {
        Authorization: `Bearer ${newToken}`,
      },
    };
    App.ready();
  },

  handleIdleAction: function () {
    // User Session has Timed Out - Terminate Session & Destroy cookie
    const logoutUrlForEnv = {
      dev: 'https://sandbox-api.nwea.org/cl-support/adult/logout',
      int: 'https://int-api.nwea.org/cl-support/adult/logout',
      prd: 'https://api.nwea.org/cl-support/adult/logout',
    };

    if (window.location.hostname.includes('sandbox')) {
      window.location.href = logoutUrlForEnv.dev;
    } else if (window.location.hostname.includes('int-api')) {
      window.location.href = logoutUrlForEnv.int;
    } else {
      window.location.href = logoutUrlForEnv.prd;
    }
  },

  hideFocusOutline: function () {
    // Remove focus ring on items that normally have it when the user clicks it.
    App.ROOT.classList.add(styles['user-is-clicking']);
  },

  allowFocusOutline: function () {
    // Leave the focus ring in place when the user is doing any keyboard work.
    App.ROOT.classList.remove(styles['user-is-clicking']);
  },

  render: function (element) {
    ReactDOM.render(element, document.getElementById('root'));

    return this;
  },
};

window.onload = () => {
  App.init();
};
